import {
  replaceContextUrlMatchers,
  type ContextUrlMatcherDescriptor,
} from '@reflex/ui/client/context-url-matchers';
import type { PluginManifest } from './plugin-registry';

/** The manifests array the last pass ran against; see `content-scanners.tsx`. */
let lastManifests: PluginManifest[] | null = null;

/**
 * Compile every enabled plugin's context-URL declarations into the shared
 * `@reflex/ui` registry the composer consults when text is pasted, so a pasted
 * item link becomes the same context chip its `@`-mention would.
 *
 * The declaration rides on the `agentContextProviders` entry that already owns
 * the kind (rather than a manifest field of its own): the URL and the picker
 * must produce the same reference id, and keeping them adjacent is what makes
 * that hard to get wrong. Entries without a `urlPattern` (kinds with no
 * addressable URL) are skipped, as are ones missing `urlIdTemplate` — a chip
 * with no id would expand to nothing.
 */
export function resolveContextUrlMatchers(manifests: PluginManifest[]): void {
  if (manifests === lastManifests) return;
  lastManifests = manifests;
  const descriptors: ContextUrlMatcherDescriptor[] = [];
  const seen = new Set<string>();
  for (const manifest of manifests) {
    if (manifest.enabled === false) continue;
    for (const provider of manifest.web?.agentContextProviders ?? []) {
      if (!provider.urlPattern || !provider.urlIdTemplate) continue;
      // First registration wins per id; later duplicates are ignored.
      if (seen.has(provider.id)) continue;
      let regex: RegExp;
      try {
        regex = new RegExp(provider.urlPattern, provider.urlFlags);
      } catch {
        // Invalid pattern — skip, and say so in dev: the manifest validator
        // doesn't cover regex validity, so nothing else would report it.
        if (import.meta.env?.MODE !== 'test') {
          console.warn(
            `[context-url-matchers] plugin "${manifest.name}" provider "${provider.id}" has an invalid urlPattern: ${provider.urlPattern}`,
          );
        }
        continue;
      }
      seen.add(provider.id);
      descriptors.push({
        id: provider.id,
        kind: provider.kind,
        regex,
        idTemplate: provider.urlIdTemplate,
        order: provider.order,
      });
    }
  }
  replaceContextUrlMatchers(descriptors);
}
